import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import './ReportsAnalytics.css';

const ReportsAnalytics = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    totalOrders: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        
        // Fetch transactions for the last 12 months
        const response = await axios.get('/stock/transactions');
        const transactions = response.data.data;

        // Process monthly data
        const last12Months = Array.from({ length: 12 }, (_, i) => {
          const d = new Date();
          d.setMonth(d.getMonth() - i);
          return d.toLocaleString('default', { month: 'short' });
        }).reverse();

        const monthlyStats = last12Months.reduce((acc, month) => {
          acc[month] = { sales: 0, purchases: 0 };
          return acc;
        }, {});

        let totalRevenue = 0;
        let totalExpenses = 0;
        let totalOrders = 0;
        const productSales = {};

        transactions.forEach(transaction => {
          const date = new Date(transaction.transactionDate);
          const month = date.toLocaleString('default', { month: 'short' });
          
          if (transaction.type === 'sale') {
            monthlyStats[month].sales += transaction.total;
            totalRevenue += transaction.total;
            totalOrders++;

            // Track product sales
            if (!productSales[transaction.productId]) {
              productSales[transaction.productId] = {
                id: transaction.productId,
                name: transaction.productName,
                sales: 0,
                quantity: 0
              };
            }
            productSales[transaction.productId].sales += transaction.total;
            productSales[transaction.productId].quantity += transaction.quantity;
          } else if (transaction.type === 'purchase') {
            monthlyStats[month].purchases += transaction.total;
            totalExpenses += transaction.total;
          }
        });

        // Format monthly data for chart
        const formattedMonthlyData = last12Months.map(month => ({
          month,
          sales: monthlyStats[month].sales,
          purchases: monthlyStats[month].purchases
        }));

        // Get top products by sales
        const topProductsList = Object.values(productSales)
          .sort((a, b) => b.sales - a.sales)
          .slice(0, 5)
          .map(product => ({
            ...product,
            percentage: ((product.sales / totalRevenue) * 100).toFixed(1)
          }));

        setMonthlyData(formattedMonthlyData);
        setTopProducts(topProductsList);
        setSummaryStats({
          totalRevenue,
          totalExpenses,
          netProfit: totalRevenue - totalExpenses,
          totalOrders
        });

      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  if (loading) {
    return <div className="loading">Loading analytics data...</div>;
  }

  return (
    <div className="analytics-container">
      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p>${summaryStats.totalRevenue.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <h3>Total Expenses</h3>
          <p>${summaryStats.totalExpenses.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <h3>Net Profit</h3>
          <p className={summaryStats.netProfit >= 0 ? 'positive' : 'negative'}>
            ${Math.abs(summaryStats.netProfit).toLocaleString()}
          </p>
        </div>
        <div className="stat-card">
          <h3>Total Orders</h3>
          <p>{summaryStats.totalOrders.toLocaleString()}</p>
        </div>
      </div>

      {/* Monthly Sales & Purchases Chart */}
      <div className="chart-container">
        <h3>Monthly Sales & Purchases</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="sales" name="Sales" fill="#4CAF50" />
            <Bar dataKey="purchases" name="Purchases" fill="#2196F3" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Products */}
      <div className="top-products">
        <h3>Top Selling Products</h3>
        <div className="products-list">
          {topProducts.map((product, index) => (
            <div key={product.id} className="product-item">
              <div className="product-rank">{index + 1}</div>
              <div className="product-info">
                <h4>{product.name}</h4>
                <div className="product-stats">
                  <span>Sales: ${product.sales.toLocaleString()}</span>
                  <span>Quantity: {product.quantity}</span>
                  <span>{product.percentage}% of total sales</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress" 
                    style={{ width: `${product.percentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportsAnalytics; 